import { makeAutoObservable, observable, reaction, runInAction } from "mobx";
import { Game } from "../models/Game";
import {agent} from "../api/agent";
import {format} from 'date-fns'
import { store } from "./store";
import { Profile, UserGame } from "../models/Profile";
import { UserStatusInGameDTO } from "../models/UserStatusInGameDTO";
import { Pagination, PagingParams } from "../models/Pagination";
import { GameFollowingDTO } from "../models/GameFollowingDTO";
import { toast } from "react-toastify";
import { GameLockDTO } from "../models/GameLockDTO";
import { AvailableSlotsRequestDTO, AvailableSlotsResponseDTO } from "../models/AvailableGameSlots";
import i18n from "../../i18n";

export default class GameStore {


    gameRegistry = new Map<string, Game>();
    selectedGame: Game | undefined = undefined;
    editMode = false;
    loading = false;
    loadingInitial = false;
    loadingDeleteGame = false;
    pagination: Pagination | null=null;
    pagingParams=new PagingParams ();
    predicate = new Map().set('all', true)
                         .set('distance', 50);
    gameFollowingLoading = false;
    gameAttentingGuid = '889ddd20-3c8b-421a-bb75-b09d70163af8';
    gameLockLoading = false;

    //available game slots handle
    availableSlots: AvailableSlotsResponseDTO | null = null;
    loadingAvailableSlots = false;

    constructor() {
        makeAutoObservable(this);
    }

    

    setPagingParams = (pagingParams:PagingParams)=>{
        this.pagingParams = pagingParams;
    }

    setPredicate = (predicate: string, value: string | Date) => {
        // Only reset predicates if explicitly requested
        if (predicate === 'reset') {
            this.predicate.forEach((_, key) => {
                if (key !== 'startDate') this.predicate.delete(key);
            });
            return;
        }
        
        // For all other cases, just set or update the predicate without resetting others
        switch (predicate) {
            case 'all':
                this.predicate.set('all', true);
                break;
            case 'isGoing':
                this.predicate.set('isGoing', true);
                break;
            case 'isHost':
                this.predicate.set('isHost', true);
                break;
            case 'isFollowing':
                this.predicate.set('isFollowing', true);
                break;
            case 'startDate':
                this.predicate.delete('startDate');
                this.predicate.set('startDate', value);
                break;
            case 'sport':
                this.predicate.delete('sport');
                this.predicate.set('sport', value);
                break;
            case 'gameType':
                this.predicate.delete('gameType');
                this.predicate.set('gameType', value);
                break;
            case 'payment':
                this.predicate.delete('payment');
                this.predicate.set('payment', value);
                break;
            case 'inclusion':
                this.predicate.delete('inclusion');
                this.predicate.set('inclusion', value);
                break;
            case 'distance':
                this.predicate.delete('distance');
                this.predicate.set('distance', value);
                break;
            case 'isLocked':
                this.predicate.delete('isLocked');
                this.predicate.set('isLocked', value);
                break;
            case 'needsOneMore':
                    this.predicate.delete('needsOneMore');
                    this.predicate.set('needsOneMore', value);
                    break;    
            case 'sortField':
                this.predicate.delete('sortField');
                this.predicate.set('sortField', value);
                break;
            case 'sortDirection':
                this.predicate.delete('sortDirection');
                this.predicate.set('sortDirection', value);
                break;    
        }
    }

    applyFilters = () => {
        this.pagingParams = new PagingParams();
        this.gameRegistry.clear();
        this.loadGames();
    }

    reset = () => {
        this.gameRegistry.clear();
        this.selectedGame = undefined;
        this.editMode = false;
        this.loading = false;
        this.loadingInitial = false;
        this.loadingDeleteGame = false;
        this.pagination = null;
        this.pagingParams = new PagingParams();
        this.predicate = new Map()
        .set('all', true)
        .set('distance', 50);
    };
    get axiosParams() {
        const params = new URLSearchParams();
        params.append('pageNumber', this.pagingParams.pageNumber.toString());
        params.append('pageSize', this.pagingParams.pageSize.toString());
        
        this.predicate.forEach((value, key) => {
            if (key === 'startDate') {
                // Format date correctly for backend
                params.append(key, (value as Date).toISOString());
            } 
            
            else {
                // For boolean or other values
                params.append(key, value.toString());
            }
        });
        
        return params;
    }

    get gamesByDate() {
        // Don't re-sort if server-side sorting is being used
        if (this.predicate.has('sortField') && this.predicate.has('sortDirection')) {
            return Array.from(this.gameRegistry.values());
        }
        
        // Fall back to default sorting otherwise
        return Array.from(this.gameRegistry.values()).sort((a, b) =>
            a.dateTimeStart!.getTime() - b.dateTimeStart!.getTime()
        );
    }

    get groupedGames(){

        return Object.entries(

            this.gamesByDate.reduce((games, game) =>{

                const dateTimeStart = format(game.dateTimeStart!, 'dd MMM yyyy');
                games[dateTimeStart] = games[dateTimeStart] ? [...games[dateTimeStart], game] : [game];
                return games;

            },{} as {[key: string]: Game[]}
            ) 
        )
    }

    loadGames = async () => {

        this.setLoadingInitial(true);

        try {  
            const result = await agent.Games.list(this.axiosParams);
            result.data.forEach(game => {
                this.setGame(game);
            })

            this.setPagination(result.pagination);

            this.setLoadingInitial(false);

        } catch (error) {
            console.log(error);
            this.setLoadingInitial(false);
        }

    }

    setPagination = (pagination:Pagination) =>{
        this.pagination = pagination;
    }

    loadGame = async (id: string, force: boolean = false) => {
        if (!force) {
            let game = this.getGame(id);
            if (game) {
                this.selectedGame = game;

                return game;
            }
        }
    
        this.loadingInitial = true;
        try {
            const game = await agent.Games.details(id);
            runInAction(() => {
                this.setGame(game);
                this.selectedGame = game;
                this.setLoadingInitial(false);
            });
            return game;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.setLoadingInitial(false);
            });
        }
    }

    private setGame = (game: Game) =>{

        const user = store.userStore.user;       

        if(user)
        {
            game.isGoing = game.gameParticipants!.some(
                (g:Profile) => g.userID === user.userID && g.userStatusInGameID === this.gameAttentingGuid
            );

            game.isHost = game.host?.userID === user.userID;

        }

        game.dateTimeStart = new Date(game.dateTimeStart!);
        game.dateTimeEnd = new Date(game.dateTimeEnd!);
        const existingGame = this.gameRegistry.get(game.id);
        if (existingGame) {
            // Keep the following status from the registry if it exists
            game.isGameFollowing = existingGame.isGameFollowing;
        }
        this.gameRegistry.set(game.id, game);
        
    }

    private getGame = (id:string) =>{

        return this.gameRegistry.get(id);

    }


    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    }

    createGame = async (game: Game) => {
        this.loading = true;
    
        try {
            const user = store.userStore.user;
            if (!user) throw new Error((i18n as any).t('game.errors.userNotAuthenticated'));
    
            const currentHost = new Profile(user);
    
            // ✅ This is required for the backend to know the host
            game.host = currentHost;
    
            // Normalize optional fields
            game.gameLocationID = null;
    
            if (!game.companySportVenueID) game.companySportVenueID = null;
            if (!game.createdAt || typeof game.createdAt === 'string') game.createdAt = null;
            if (!game.modifiedAt || typeof game.modifiedAt === 'string') game.modifiedAt = null;
            if (!game.modifiedBy || game.modifiedBy === '') {
                game.modifiedBy = '00000000-0000-0000-0000-000000000000';
            }
    
            await agent.Games.create(this.prepareGameForSave(game));
            
    
            runInAction(() => {
                // ✅ This ensures the local object is updated correctly for UI
                game.host = currentHost;
                game.isHost = true;
                game.hostImageUrl = currentHost.image;
                game.hostUsername = currentHost.username; 
                game.hostUserID = currentHost.userID; 
                game.hostFullName = currentHost.firstName + ' ' + currentHost.lastName;  
                this.gameRegistry.set(game.id, observable(game));
                this.selectedGame = game;
                this.editMode = false;
                this.loading = false;
            });
    
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    updateGame = async (game: Game) => {


        if (game.companySportVenueID === '') {
            game.companySportVenueID = null;
          }
      
          if (game.createdAt === '') {
            game.createdAt = null;
          }
      
          if (game.modifiedAt === '') {
            game.modifiedAt = null;
          }
      
          if (game.modifiedBy === '') {
            game.modifiedBy = '00000000-0000-0000-0000-000000000000';
          }

        this.loading = true;
        try {
            await agent.Games.update(this.prepareGameForSave(game));
            runInAction(()=>{
                
                this.gameRegistry.set(game.id, observable(game));
                this.selectedGame = game;
                this.editMode = false;
                this.loading = false;

            });
        } catch (error) {
            console.log(error);
            runInAction(()=>{
                toast.error((i18n as any).t('game.errors.updateFailed'));
                console.log(error);
                this.loading = false;

            });
        }
    }

    deleteGame = async (id: string) => {
        this.loadingDeleteGame = true;
        try {
            await agent.Games.delete(id);
            runInAction(() => {
             this.gameRegistry.delete(id);
             toast.success((i18n as any).t('game.success.gameDeleted'));
                this.loadingDeleteGame = false;
            })
        } catch (error) {
            console.log(error);
            toast.error((i18n as any).t('game.errors.deleteFailed'));
            runInAction(() => {
                this.loadingDeleteGame = false;
            })
        }
    }

    updateGameLocking = async (gameLockDTO: GameLockDTO) => {
        this.gameLockLoading = true;
    
        try {
            await agent.Games.gameLock(gameLockDTO);
            runInAction(() => {
                const currentGame = this.selectedGame;
                if (currentGame && currentGame.id === gameLockDTO.ID) {
                    const updatedGame = {
                        ...currentGame,
                        isLocked: !currentGame.isLocked,
                    };
                    this.selectedGame = updatedGame;
                    this.gameRegistry.set(updatedGame.id, updatedGame);
                    toast.success(updatedGame.isLocked ? 
                        (i18n as any).t('game.success.gameLocked') : 
                        (i18n as any).t('game.success.gameUnlocked')
                    );
                }
                this.gameLockLoading = false;
            });
        } catch (error) {
            console.error("Error locking/unlocking game:", error);
            toast.error((i18n as any).t('game.errors.lockStatusFailed'));
            runInAction(() => {
                this.gameLockLoading = false;
            });
        }
    }
    
    updateAttendence = async () => {
        const user = store.userStore.user;
        this.loading = true;
    
        try {
            let userStatusInGameDTO: UserStatusInGameDTO;
    
            if (this.selectedGame?.isGoing) {
                userStatusInGameDTO = { ID: '06fbe407-9c55-43ff-a0cc-7e58ee960a4f', StatusName: 'Not Attending' };
            } else {
                userStatusInGameDTO = { ID: '889ddd20-3c8b-421a-bb75-b09d70163af8', StatusName: 'Attending' };
            }
    
            await agent.Games.attend(this.selectedGame!.id, userStatusInGameDTO);
    
            runInAction(() => {
                let participantCountChange = 0;
    
                if (this.selectedGame?.isGoing) {
                    // User is leaving the game
                    this.selectedGame.gameParticipants = this.selectedGame.gameParticipants?.filter(
                        (g: { userID: string | undefined }) => g.userID !== user?.userID
                    );
                    this.selectedGame.isGoing = false;
                    participantCountChange = -1;
                    toast.success((i18n as any).t('game.success.attendanceCancelled'));
                } else {
                    // User is joining the game
                    const participant = new Profile(user!);
                    participant.sports = store.profileStore.profile?.sports ?? [];
                    this.selectedGame!.isGoing = true;
                    if (!this.selectedGame?.gameParticipants) {
                        this.selectedGame!.gameParticipants = [];
                    }
                    this.selectedGame?.gameParticipants!.push(participant);
                    participantCountChange = 1;
                    toast.success((i18n as any).t('game.success.joinedGame'));
                }
    
                // Auto lock/unlock based on participant count
                if (this.selectedGame && this.selectedGame.maxNumberOfParticipants > 0) {
                    const currentParticipantCount = this.selectedGame.gameParticipants?.length || 0;
                    const wasLocked = this.selectedGame.isLocked;
    
                    if (participantCountChange > 0 && currentParticipantCount >= this.selectedGame.maxNumberOfParticipants && !wasLocked) {
                        this.selectedGame.isLocked = true;
                        toast.info((i18n as any).t('game.success.gameLocked'));
                    } else if (participantCountChange < 0 && currentParticipantCount < this.selectedGame.maxNumberOfParticipants && wasLocked) {
                        this.selectedGame.isLocked = false;
                        toast.info((i18n as any).t('game.success.gameUnlocked'));
                    }
                }
    
                this.gameRegistry.set(this.selectedGame!.id, this.selectedGame!);
            });
    
            // eload video links after participation status changes ──────────
            // The server filters visible links based on participation, so we need a
            // fresh fetch whenever isGoing flips — joining reveals participant-only
            // videos; leaving hides them again.
            if (this.selectedGame?.id) {
                await store.gameVideoLinkStore.loadVideoLinks(this.selectedGame.id); 
            }
            // ───────────────────────────────────────────────────────────────────────
    
        } catch (error) {
            console.log(error);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    clearSelectedGame=()=>{

        this.selectedGame = undefined;

    }

    updateParticipantFollowing = (userID:string)=>{

        this.gameRegistry.forEach(game=>{
            game.gameParticipants?.forEach(participant=>{
                if(participant.userID === userID)
                {
                    participant.following? participant.followersCount-- : participant.followersCount++;
                    participant.following = !participant.following;
                } 
            })

        })
    }
    

    updateGameFollowing = async (gameFollowingDTO: GameFollowingDTO) => {
        this.gameFollowingLoading = true;
        try {
            await agent.Games.gameFollow(gameFollowingDTO);
            runInAction(() => {
                const currentGame = this.selectedGame;
                if (currentGame && currentGame.id === gameFollowingDTO.id) {
                    const updatedGame = {
                        ...currentGame,
                        isGameFollowing: !currentGame.isGameFollowing,
                    };
                    this.selectedGame = updatedGame;
                    this.gameRegistry.set(updatedGame.id, updatedGame);
                } else {
                    const game = this.gameRegistry.get(gameFollowingDTO.id);
                    if (game) {
                        const updatedGame = {
                            ...game,
                            isGameFollowing: !game.isGameFollowing,
                        };
                        this.gameRegistry.set(gameFollowingDTO.id, updatedGame);
                    }
                }
            
                this.gameFollowingLoading = false;
                toast.success((i18n as any).t('game.success.followingChanged'));
            });
        } catch (error) {
            console.log(error);
            toast.error((i18n as any).t('game.errors.followingFailed'));
            runInAction(() => this.gameFollowingLoading = false);
        }
    };

      updateGameParticipantFollowingArray = (id:string)=>{

        this.gameRegistry.forEach(game=>{

                if(game.id === id)
                {
                    game.isGameFollowing = !game.isGameFollowing;
                } 


        })
    }

    findAvailableSlots = async (request: AvailableSlotsRequestDTO) => {
        this.loadingAvailableSlots = true;
        try {
            const response = await agent.Games.findAvailableSlots(request);
            runInAction(() => {
                // Convert UTC times to local times using the same pattern
                const processedResponse = {
                    ...response,
                    availableSlots: response.availableSlots.map(slot => ({
                        ...slot,
                        availableSlots: slot.availableSlots.map(timeSlot => {
                            // If backend returns full datetime strings, use this:
                            if (timeSlot.startTime.includes('T') || timeSlot.startTime.includes('Z')) {
                                return {
                                    ...timeSlot,
                                    startTime: new Date(timeSlot.startTime).toLocaleTimeString('en-US', { 
                                        hour12: false, 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                    }),
                                    endTime: new Date(timeSlot.endTime).toLocaleTimeString('en-US', { 
                                        hour12: false, 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                    })
                                };
                            } else {
                                // If backend returns just "HH:mm", treat as local time
                                // Remove the 'Z' suffix to avoid UTC conversion
                                const startDateTime = new Date(`${response.searchDate}T${timeSlot.startTime}:00.000`);
                                const endDateTime = new Date(`${response.searchDate}T${timeSlot.endTime}:00.000`);
                                
                                return {
                                    ...timeSlot,
                                    startTime: startDateTime.toLocaleTimeString('en-US', { 
                                        hour12: false, 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                    }),
                                    endTime: endDateTime.toLocaleTimeString('en-US', { 
                                        hour12: false, 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                    })
                                };
                            }
                        })
                    }))
                };
                
                this.availableSlots = processedResponse;
                this.loadingAvailableSlots = false;
            });
            return response;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loadingAvailableSlots = false;
            });
            toast.error((i18n as any).t('game.errors.availableSlotsFailed'));
            throw error;
        }
    };

    clearAvailableSlots = () => {
        this.availableSlots = null;
    };

    private prepareGameForSave = (game: Game): Game => {
        return {
            ...game,
            dateTimeStart: game.dateTimeStart ? new Date(game.dateTimeStart).toISOString() : null,
            dateTimeEnd: game.dateTimeEnd ? new Date(game.dateTimeEnd).toISOString() : null
        } as Game;
    }

}